// Unit Conversion System for PlateProfit

// Unit categories
export const UNIT_TYPES = {
  MASS: "mass",
  VOLUME: "volume",
  COUNT: "count"
};

// All supported units with their base unit and conversion factor
export const UNITS = {
  // Mass units (base: grams)
  "g": { type: UNIT_TYPES.MASS, base: "g", factor: 1, name: "Gram" },
  "kg": { type: UNIT_TYPES.MASS, base: "g", factor: 1000, name: "Kilogram" },
  "mg": { type: UNIT_TYPES.MASS, base: "g", factor: 0.001, name: "Milligram" },
  "oz": { type: UNIT_TYPES.MASS, base: "g", factor: 28.3495, name: "Ounce" },
  "lb": { type: UNIT_TYPES.MASS, base: "g", factor: 453.592, name: "Pound" },
  
  // Volume units (base: milliliters)
  "ml": { type: UNIT_TYPES.VOLUME, base: "ml", factor: 1, name: "Milliliter" },
  "L": { type: UNIT_TYPES.VOLUME, base: "ml", factor: 1000, name: "Liter" },
  "fl oz": { type: UNIT_TYPES.VOLUME, base: "ml", factor: 29.5735, name: "Fluid Ounce" },
  "cup": { type: UNIT_TYPES.VOLUME, base: "ml", factor: 236.588, name: "Cup" },
  "tbsp": { type: UNIT_TYPES.VOLUME, base: "ml", factor: 14.7868, name: "Tablespoon" },
  "tsp": { type: UNIT_TYPES.VOLUME, base: "ml", factor: 4.92892, name: "Teaspoon" },
  
  // Count units (base: each)
  "each": { type: UNIT_TYPES.COUNT, base: "each", factor: 1, name: "Each" },
  "piece": { type: UNIT_TYPES.COUNT, base: "each", factor: 1, name: "Piece" },
  "unit": { type: UNIT_TYPES.COUNT, base: "each", factor: 1, name: "Unit" }
};

// Get unit information
export const getUnitInfo = (unit) => {
  return UNITS[unit] || { type: UNIT_TYPES.COUNT, base: "each", factor: 1, name: unit };
};

// Check if two units are compatible (same type)
export const areUnitsCompatible = (unit1, unit2) => {
  const info1 = getUnitInfo(unit1);
  const info2 = getUnitInfo(unit2);
  return info1.type === info2.type;
};

// Convert quantity from one unit to another
export const convertUnit = (quantity, fromUnit, toUnit) => {
  const fromInfo = getUnitInfo(fromUnit);
  const toInfo = getUnitInfo(toUnit);
  
  // Check compatibility
  if (fromInfo.type !== toInfo.type) {
    console.warn(`Cannot convert between ${fromUnit} (${fromInfo.type}) and ${toUnit} (${toInfo.type})`);
    return quantity;
  }
  
  // Convert to base unit, then to target unit
  const baseQuantity = parseFloat(quantity) * fromInfo.factor;
  const convertedQuantity = baseQuantity / toInfo.factor;
  
  return convertedQuantity;
};

// Convert to base unit for cost calculation
export const convertToBaseUnit = (quantity, unit) => {
  const unitInfo = getUnitInfo(unit);
  return parseFloat(quantity) * unitInfo.factor;
};

// Calculate cost for an ingredient with unit conversion and density support
export const calculateIngredientCost = (
  recipeQuantity,
  recipeUnit,
  ingredientUnitCost,
  ingredientUnit,
  densityFactor = null  // g/ml for volume-to-weight conversion
) => {
  const recipeInfo = getUnitInfo(recipeUnit);
  const ingredientInfo = getUnitInfo(ingredientUnit);
  
  // Case 1: Same unit type - direct conversion
  if (recipeInfo.type === ingredientInfo.type) {
    const convertedQuantity = convertUnit(recipeQuantity, recipeUnit, ingredientUnit);
    return convertedQuantity * parseFloat(ingredientUnitCost);
  }
  
  // Case 2: Volume to Weight conversion using density (Golden Thread)
  if (recipeInfo.type === UNIT_TYPES.VOLUME && ingredientInfo.type === UNIT_TYPES.MASS && densityFactor) {
    // Convert recipe volume to ml
    const volumeInMl = convertToBaseUnit(recipeQuantity, recipeUnit);
    
    // Convert ml to grams using density: weight(g) = volume(ml) × density(g/ml)
    const weightInGrams = volumeInMl * parseFloat(densityFactor);
    
    // Convert grams to ingredient's unit
    const convertedQuantity = convertUnit(weightInGrams, 'g', ingredientUnit);
    
    // Calculate cost
    return convertedQuantity * parseFloat(ingredientUnitCost);
  }
  
  // Case 3: Weight to Volume conversion using density
  if (recipeInfo.type === UNIT_TYPES.MASS && ingredientInfo.type === UNIT_TYPES.VOLUME && densityFactor) {
    // Convert recipe weight to grams
    const weightInGrams = convertToBaseUnit(recipeQuantity, recipeUnit);
    
    // Convert grams to ml using density: volume(ml) = weight(g) ÷ density(g/ml)
    const volumeInMl = weightInGrams / parseFloat(densityFactor);
    
    // Convert ml to ingredient's unit
    const convertedQuantity = convertUnit(volumeInMl, 'ml', ingredientUnit);
    
    // Calculate cost
    return convertedQuantity * parseFloat(ingredientUnitCost);
  }
  
  // No valid conversion found
  console.warn(`Cannot convert ${recipeUnit} (${recipeInfo.type}) to ${ingredientUnit} (${ingredientInfo.type})${densityFactor ? '' : ' - density factor required'}`);
  return 0;
};

// Get units grouped by type
export const getUnitsByType = () => {
  const grouped = {
    [UNIT_TYPES.MASS]: [],
    [UNIT_TYPES.VOLUME]: [],
    [UNIT_TYPES.COUNT]: []
  };
  
  Object.entries(UNITS).forEach(([unit, info]) => {
    grouped[info.type].push({ unit, ...info });
  });
  
  return grouped;
};

// Get all units as a flat list
export const getAllUnits = () => {
  return Object.keys(UNITS);
};

// Format unit display
export const formatUnitDisplay = (unit) => {
  const info = getUnitInfo(unit);
  return `${unit} (${info.name})`;
};
